// EditUserModal.tsx — Co Marca — Editar Usuario
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit, Mail, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { updateUser } from '../../services/userService';
import { fetchUserLevels } from '../../services/userLevelService';

interface UserLevel { id: number; nombre: string; activo: boolean; }
interface EditUserModalProps {
    user: any;
    onClose: () => void;
    onUserUpdated: (updatedUser: any) => void;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.eum-modal * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
@keyframes eumFadeIn  { from{opacity:0;} to{opacity:1;} }
@keyframes eumSlideUp { from{opacity:0;transform:translateY(24px) scale(.98);} to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes spin { to{transform:rotate(360deg);} }
.eum-overlay { animation:eumFadeIn .22s ease both; }
.eum-dialog  { animation:eumSlideUp .32s cubic-bezier(.16,1,.3,1) both; }
.eum-input {
    width:100%; padding:10px 14px; border-radius:10px;
    border:1.5px solid #e5e7eb; background:#fafafa;
    font-size:.88rem; color:#374151; outline:none; transition:all .2s;
}
.eum-input:focus { border-color:${colors.secondary}; box-shadow:0 0 0 3px ${colors.secondary}12; background:#fff; }
.eum-select { appearance:none; }
.eum-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.secondary},#004d7a);
    color:#fff; border:none; border-radius:11px;
    padding:10px 22px; font-size:.88rem; font-weight:800;
    cursor:pointer; box-shadow:0 4px 14px rgba(97, 37, 123,.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.eum-btn-primary:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 22px rgba(97, 37, 123,.34); }
.eum-btn-primary:disabled { background:#e2e8f0; color:#94a3b8; box-shadow:none; cursor:not-allowed; }
.eum-btn-ghost {
    display:inline-flex; align-items:center; gap:7px;
    background:#f8fafc; border:1.5px solid #e2e8f0;
    color:#374151; border-radius:11px;
    padding:10px 18px; font-size:.88rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.eum-btn-ghost:hover { background:#f1f5f9; }
.custom-scroll::-webkit-scrollbar { width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background:rgba(97, 37, 123,.3); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background:rgba(97, 37, 123,.5); }
`;

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onUserUpdated }) => {
    const [firstName, setFirstName]     = useState(user.first_name||'');
    const [lastName, setLastName]       = useState(user.last_name||'');
    const [email, setEmail]             = useState(user.email||'');
    const [isActive, setIsActive]       = useState(user.estado||false);
    const [selectedLevel, setSelectedLevel] = useState<number|null>(user.tipo_nivel_usuario?.id||null);
    const [userLevels, setUserLevels]   = useState<UserLevel[]>([]);
    const [levelsLoading, setLevelsLoading] = useState(true);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState<string|null>(null);

    useEffect(() => {
        setLevelsLoading(true);
        fetchUserLevels().then(setUserLevels).catch(console.error).finally(()=>setLevelsLoading(false));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true); setError(null);
        try {
            const updated = await updateUser(user.id, firstName, lastName, email, isActive, selectedLevel);
            onUserUpdated(updated); onClose();
        } catch (err: any) { setError(err.message); }
        finally { setLoading(false); }
    };

    return createPortal(
        <>
            <style>{css}</style>
            <div className="eum-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="eum-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>

                <div className="eum-dialog custom-scroll" style={{ position:'relative', background:'#fff', borderRadius:'24px', width:'100%', maxWidth:'560px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid #f1f5f9' }}>

                    {/* Barra top — azul para editar */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.secondary},${colors.secondaryLight},${colors.accentDark},${colors.primary})`, borderRadius:'24px 24px 0 0' }}/>

                    {/* Header */}
                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.secondary},#004d7a)`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px ${colors.secondary}30` }}>
                                    <Edit size={17} color="#fff"/>
                                </div>
                                <div>
                                    <h3 style={{ fontSize:'1.1rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Editar Usuario</h3>
                                    <p style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500' }}>Modificando <strong style={{ color:'#374151' }}>{user.first_name} {user.last_name}</strong></p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px' }}>
                                {[colors.secondary,colors.secondaryLight,colors.accentDark,colors.primary].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={15} color="#64748b"/>
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'16px' }}>

                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`${colors.primary}08`, border:`1.5px solid ${colors.primary}22`, borderRadius:'10px', padding:'10px 14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:colors.primaryDark, fontWeight:'600' }}>{error}</span>
                            </div>
                        )}

                        {/* Nombre + Apellido */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Nombre *</label>
                                <input type="text" required value={firstName} onChange={e=>setFirstName(e.target.value)} className="eum-input" placeholder="Nombre"/>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Apellido *</label>
                                <input type="text" required value={lastName} onChange={e=>setLastName(e.target.value)} className="eum-input" placeholder="Apellido"/>
                            </div>
                        </div>

                        {/* Email + Nivel */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Mail size={11} color={colors.secondary}/> Email *
                                </label>
                                <input type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="eum-input" placeholder="correo@ejemplo.com"/>
                            </div>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <ShieldCheck size={11} color={colors.accentDark}/> Nivel
                                </label>
                                <select value={selectedLevel??''} onChange={e=>setSelectedLevel(Number(e.target.value)||null)} disabled={levelsLoading} className="eum-input eum-select">
                                    <option value="">{levelsLoading?'Cargando...':'Seleccionar nivel'}</option>
                                    {userLevels.filter(l=>l.activo).sort((a,b)=>a.id-b.id).map(l=>(
                                        <option key={l.id} value={l.id}>{l.nombre}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Estado */}
                        <div style={{ background:'#f8fafc', border:'1.5px solid #f1f5f9', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                            <div>
                                <p style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a', marginBottom:'2px' }}>Estado del usuario</p>
                                <p style={{ fontSize:'.7rem', color:isActive?colors.secondaryLight:'#94a3b8', fontWeight:'600' }}>
                                    {isActive?'● Activo':'○ Inactivo'}
                                </p>
                            </div>
                            <div onClick={()=>setIsActive(!isActive)} style={{ width:'44px', height:'24px', borderRadius:'100px', background:isActive?colors.secondary:'#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                <div style={{ position:'absolute', top:'3px', left:isActive?'23px':'3px', width:'18px', height:'18px', borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} className="eum-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={loading} className="eum-btn-primary">
                                {loading
                                    ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Guardando...</>
                                    : <><CheckCircle2 size={14}/> Guardar Cambios</>
                                }
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>,
        document.body
    );
};

export default EditUserModal;